import React, { useMemo, useState } from 'react';
import { Box, BoxProps, Container, ContainerProps, Grid, GridProps, IconButton, Stack, styled, Typography, useTheme } from '@mui/material';
import { useParams } from 'react-router-dom';
import { TitleBar } from 'components/Layouts/TitleBar';
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet-async';
import { useRecoilValue } from 'recoil';
import { tenantInfoState } from 'modules/application/atoms';
import { ServiceCallInformation } from '../components/ServiceCallPage/ServiceCallInformation';
import { useQuery } from '@tanstack/react-query';
import { ServiceCallsApi } from '../api';
import { LoadingPage } from 'modules/application/components/LoadingPage';
import { useDefaultErrorHandler } from 'api';
import { ServiceCallNotesSection } from '../components/ServiceCallPage/ServiceCallNotesSection';
import { MobileSlideInDrawer } from 'components/ResponsiveDrawer';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const PageBody = styled(Box)<BoxProps>(({ theme }) => ({
  background: `linear-gradient(270deg,${theme.palette.background.paper},${theme.palette.background.paper} 50%,hsla(0,0%,100%,0) 0,hsla(0,0%,100%,0))`,
  display: 'flex',
  flexGrow: 1,
  [theme.breakpoints.down('md')]: {
    backgroundColor: theme.palette.background.default,
  }
}));

const PageContainer = styled(Container)<ContainerProps>(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    backgroundColor: theme.palette.background.default,
  }
}));

const LeftGrid = styled(Grid)<GridProps>(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  [theme.breakpoints.down('md')]: {
    paddingRight: 0
  }
}));

const RightGrid = styled(Grid)<GridProps>(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderLeftStyle: 'solid',
  borderLeftWidth: '1px',
  borderLeftColor: theme.palette.grey[300],
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

export const ServiceCallPage = () => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const tenantInfo = useRecoilValue(tenantInfoState);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isMobileDrawerClosed, setisMobileDrawerClosed] = useState(true);

  const { serviceCallId } = useParams();
  const serviceCallQuery = useQuery(['service-call', serviceCallId], () => ServiceCallsApi.get(serviceCallId ?? ''), { retry: false, onError: useDefaultErrorHandler() });
  const pageTitle = useMemo(() => `${formatMessage({ id: 'ServiceCallPage.Title', defaultMessage: 'Service call' })} ${serviceCallQuery.data?.externalId}`, [formatMessage, serviceCallQuery]);

  const handleMobileDrawerOpenButtonClicked = () => {
    window.scrollTo(0, 0);
    setIsMobileDrawerOpen(true);
    setisMobileDrawerClosed(false);
  };

  const handleOnMobileDrawerClose = () => {
    setIsMobileDrawerOpen(false);
  };

  const handleOnMobileDrawerClosed = () => {
    setisMobileDrawerClosed(true);
  };
  return (
    <>
      <Helmet>
        <title>{formatMessage({ id: 'ServiceCallPage.Title', defaultMessage: 'Service Call' })} - {tenantInfo.name}</title>
        <meta name="description" content={formatMessage({ id: 'ServiceCallPage.Description', defaultMessage: 'Manage service call' })} />
      </Helmet>
      {serviceCallQuery.isFetching && <LoadingPage />}
      {serviceCallQuery.isFetched && serviceCallQuery.data && serviceCallId &&
        <>
          <TitleBar title={pageTitle} subtitle={serviceCallQuery.data.reason.title} zIndex={!isMobileDrawerClosed ? theme.zIndex.drawer + 1 : undefined} />
          <Container sx={{ display: { sx: 'inherit', md: 'none' }, backgroundColor: 'background.paper' }}>
            <Stack direction='row' alignItems='center' justifyContent='space-between' py={2}>
              <Typography>{formatMessage({ id: 'ServiceCallPage.DrawerOpenMessage', defaultMessage: 'Service call details' })}</Typography>
              <IconButton color="primary" aria-label="Find ticket" onClick={handleMobileDrawerOpenButtonClicked}>
                <ArrowForwardIcon />
              </IconButton>
            </Stack>
          </Container>
          <PageBody width='100%'>
            <PageContainer maxWidth='xl'>
              <Grid container height='100%'>
                <LeftGrid item xs={12} md={8} py={4} paddingRight={4}>
                  <ServiceCallNotesSection serviceCallId={serviceCallId} notes={serviceCallQuery.data.notes} />
                </LeftGrid>
                <RightGrid item md={4} py={4} paddingLeft={4}>
                  <ServiceCallInformation serviceCall={serviceCallQuery.data} />
                </RightGrid>
              </Grid>
            </PageContainer>
          </PageBody>
          <MobileSlideInDrawer
            isOpen={isMobileDrawerOpen}
            onClose={handleOnMobileDrawerClose}
            onClosed={handleOnMobileDrawerClosed}
            title={pageTitle}
            subTitle={serviceCallQuery.data.reason.title}
            closeButtonText={formatMessage({ id: 'ServiceCallPage.DrawerCloseMessage', defaultMessage: 'Leave a message' })}
            invertColors={true}
          >
            <ServiceCallInformation serviceCall={serviceCallQuery.data} />
          </MobileSlideInDrawer>
        </>
      }
    </>
  );
};